<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ChannelSelector from '../ChannelSelector.vue';

const props = defineProps({
  channel: {
    type: Object,
    required: true,
  },
  enabledFeatures: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['channelItemClick']);
const { t } = useI18n();

const hasFbConfigured = computed(() => {
  return window.chatwootConfig?.fbAppId;
});

const hasInstagramConfigured = computed(() => {
  return window.chatwootConfig?.instagramAppId;
});

const hasTiktokConfigured = computed(() => {
  return window.chatwootConfig?.tiktokAppId;
});

const isActive = computed(() => {
  const { key } = props.channel;
  if (key === 'whatsapp_web') {
    return true;
  }

  if (Object.keys(props.enabledFeatures).length === 0) {
    return false;
  }
  if (key === 'website') {
    return props.enabledFeatures.channel_website;
  }
  if (key === 'facebook') {
    return props.enabledFeatures.channel_facebook && hasFbConfigured.value;
  }
  if (key === 'email') {
    return props.enabledFeatures.channel_email;
  }

  if (key === 'instagram') {
    return (
      props.enabledFeatures.channel_instagram && hasInstagramConfigured.value
    );
  }

  if (key === 'tiktok') {
    return props.enabledFeatures.channel_tiktok && hasTiktokConfigured.value;
  }

  // Manual Cloud API calling setup no longer depends on embedded Meta App ID.
  if (key === 'voice' || key === 'whatsapp_call') {
    return props.enabledFeatures.channel_voice;
  }

  return [
    'website',
    'twilio',
    'api',
    'whatsapp',
    'whatsapp_web',
    'sms',
    'telegram',
    'line',
    'instagram',
    'tiktok',
    'voice',
  ].includes(key);
});

// Canais que, além da liberação na conta, dependem de um app configurado
// no servidor (Meta, TikTok).
const APP_CONFIG_BY_CHANNEL = {
  facebook: hasFbConfigured,
  instagram: hasInstagramConfigured,
  tiktok: hasTiktokConfigured,
};

const FEATURE_BY_CHANNEL = {
  website: 'channel_website',
  facebook: 'channel_facebook',
  email: 'channel_email',
  instagram: 'channel_instagram',
  tiktok: 'channel_tiktok',
  voice: 'channel_voice',
  whatsapp_call: 'channel_voice',
};

const isComingSoon = computed(() => {
  const { key } = props.channel;
  // Show "Coming Soon" only if the channel is marked as coming soon
  // and the corresponding feature flag is not enabled yet.
  return ['voice'].includes(key) && !isActive.value;
});

const isBeta = computed(() => {
  return ['tiktok', 'voice', 'whatsapp_call'].includes(props.channel.key);
});

// Card apagado precisa dizer por quê: sem isso o admin não sabe se é plano,
// configuração ou erro.
const disabledReason = computed(() => {
  if (isActive.value || isComingSoon.value) return '';
  if (Object.keys(props.enabledFeatures).length === 0) return '';

  const { key } = props.channel;
  const feature = FEATURE_BY_CHANNEL[key];
  const appConfig = APP_CONFIG_BY_CHANNEL[key];
  if (
    feature &&
    props.enabledFeatures[feature] &&
    appConfig &&
    !appConfig.value
  ) {
    return t('CHANNEL_SELECTOR.DISABLED_NOT_CONFIGURED');
  }
  return t('CHANNEL_SELECTOR.DISABLED_NOT_ENABLED');
});

const hasVoiceBadge = computed(() => {
  return (
    ['voice', 'whatsapp_call'].includes(props.channel.key) &&
    !!props.enabledFeatures.channel_voice
  );
});

const onItemClick = () => {
  if (isActive.value) {
    emit('channelItemClick', props.channel.key);
  }
};
</script>

<template>
  <ChannelSelector
    :title="channel.title"
    :description="channel.description"
    :icon="channel.icon"
    :is-coming-soon="isComingSoon"
    :is-beta="isBeta"
    :has-voice-badge="hasVoiceBadge"
    :disabled="!isActive"
    :disabled-reason="disabledReason"
    @click="onItemClick"
  />
</template>
